/**
 * Best-effort scrubbing of data leaving the browser for observability
 */

// Sensitive key names, mirroring ddtrace's default obfuscation list
// (password/passphrase variants, secret, api/private/public/access/secret
// keys, token, consumer key/secret, signature, authorization), plus OAuth
// response params (code, state) carried by the /oauth and /token-flow
// routes, which ddtrace's list does not cover.
const SENSITIVE_KEY =
  /^(?:(?:old|new)[-_]?)?p(?:ass)?w(?:or)?d\d?$|^pass(?:[-_]?phrase)?$|^secret$|^(?:api|private|public|access|secret)[-_]?key(?:[-_]?id)?$|^token$|^consumer[-_]?(?:id|key|secret)$|^sign(?:ed|ature)?$|^auth(?:entication|orization)?$|^code$|^state$/i;

function isSensitiveKey(name: string): boolean {
  try {
    return SENSITIVE_KEY.test(decodeURIComponent(name));
  } catch {
    // A malformed escape isn't a valid key, but redact rather than risk it.
    return true;
  }
}

/**
 * Redact the values of sensitive query parameters, preserving the remaining
 * query string and any hash fragment.
 */
export function redactUrlSecrets(url: string): string {
  const queryStart = url.indexOf("?");
  if (queryStart === -1) return url;
  const hashStart = url.indexOf("#", queryStart);
  const base = url.slice(0, queryStart);
  const query = url.slice(
    queryStart + 1,
    hashStart === -1 ? undefined : hashStart,
  );
  const fragment = hashStart === -1 ? "" : url.slice(hashStart);
  const redacted = query
    .split("&")
    .map((pair) => {
      const eq = pair.indexOf("=");
      const name = eq === -1 ? pair : pair.slice(0, eq);
      return isSensitiveKey(name) ? `${name}=[redacted]` : pair;
    })
    .join("&");
  return `${base}?${redacted}${fragment}`;
}

const URL_PATTERN = /https?:\/\/[^\s"')\]]+/g;

/** Redact sensitive query params in every URL embedded in free-form text. */
export function redactUrlsInText(text: string): string {
  return text.replace(URL_PATTERN, (url) => redactUrlSecrets(url));
}

// Free-form log messages: `key=value` secrets keep the rest of the line;
// `key:` secrets redact to end of line so `Authorization: Bearer xyz`
// doesn't leak the token after the scheme word.
const SECRET_ASSIGNMENT =
  /((?:token|api[-_]?key|secret|password|passwd)\s*=\s*)\S+/gi;
const SECRET_HEADER =
  /((?:token|api[-_]?key|secret|password|passwd|authorization)\s*:\s*).*/gi;

/** Redact secret-shaped key/value pairs from a free-form log message. */
export function redactSecretValues(message: string): string {
  return message
    .replace(SECRET_ASSIGNMENT, "$1[redacted]")
    .replace(SECRET_HEADER, "$1[redacted]");
}
